import { PlusIcon } from "@heroicons/react/24/outline";
import {
    ActionType,
    COLLABORATOR_SENTINEL,
    EVERYONE_SENTINEL,
    PipelineCardSort,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { useDebouncedState } from "components/shared/hooks";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { ErrorMessage } from "components/ui/error-message";
import { PrimaryButton } from "components/ui/primary-button";
import { SearchInput } from "components/ui/search-input";
import { SelectPro } from "components/ui/select-pro";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { Actions } from "store/actions";
import { ReferralPipelineAssigneeFilter } from "store/reducers/referral-pipeline-search";
import {
    useCanSeeReferralSourceOwners,
    useFeature,
    useIsAllowedInWholeTenant,
    useRegionId,
} from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { AddReferralToBoardButton } from "./add-referral-to-board";
import { ReferralPipelineBoard } from "./referral-pipeline-board";
import { ReferralPipelineBoardQuery, referralPipelineSortOptions } from "./types";

const useCanManagePipelines = (): boolean => {
    const settingsAllowed = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const managePipelinesAllowed = useIsAllowedInWholeTenant(ActionType.UpdateCustomList);
    return settingsAllowed && managePipelinesAllowed;
};

export const ReferralPipelinePage: React.FC = () => {
    const referralPipelinesEnabled = useFeature("referralPipelines");

    if (!referralPipelinesEnabled) {
        return null;
    }

    return <ReferralPipelinePageContent />;
};

const ReferralPipelinePageContent: React.FC = () => {
    const trpcContext = trpc.useTRPC();
    const history = useHistory();
    const regionId = useRegionId();
    const canManagePipelines = useCanManagePipelines();
    const canSeeReferralSourceOwners = useCanSeeReferralSourceOwners();
    const { pipelineId } = useParams<{ pipelineId?: string }>();
    const pipelinesQuery = useQuery({ ...trpcContext.pipeline.list.queryOptions() });
    const pipelines = pipelinesQuery.data;

    const dispatch = useDispatch();
    const searchState = useSelector(
        (state: ApplicationState) =>
            state.referralPipelineSearch.referralPipelineSearchInstance,
    );
    const [searchText, setSearchText, searchTextDebounced] = useDebouncedState(
        searchState.searchText,
        (value: string) => (value.length < 3 ? 750 : 600),
    );
    React.useEffect(() => {
        dispatch({
            type: Actions[Actions.REFERRAL_PIPELINES_SEARCH_TEXT],
            searchText: searchTextDebounced,
        });
    }, [dispatch, searchTextDebounced]);

    const handleSearchTextChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(e.target.value);
        },
        [setSearchText],
    );

    const handleClearSearchText = React.useCallback(() => {
        setSearchText("");
    }, [setSearchText]);

    const handleAssigneeChange = React.useCallback(
        (value: SelectableUser) => {
            let assignee: ReferralPipelineAssigneeFilter = EVERYONE_SENTINEL;
            if (value === "") {
                assignee = "";
            } else if (
                value &&
                value !== EVERYONE_SENTINEL &&
                value !== COLLABORATOR_SENTINEL
            ) {
                assignee = value;
            }
            dispatch({
                type: Actions[Actions.REFERRAL_PIPELINES_SELECT_ASSIGNEE],
                assignee,
            });
        },
        [dispatch],
    );

    const handleSortChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            dispatch({
                type: Actions[Actions.REFERRAL_PIPELINES_SORT],
                cardSort: e.target.value as PipelineCardSort,
            });
        },
        [dispatch],
    );

    const boardQuery = React.useMemo<ReferralPipelineBoardQuery>(
        () => ({
            search: searchState.searchText,
            assignee: searchState.assignee,
            sort: searchState.cardSortPreference,
        }),
        [searchState],
    );

    React.useEffect(() => {
        if (!pipelines || pipelines.length === 0) {
            return;
        }
        if (!pipelineId || !pipelines.some(p => p.id === pipelineId)) {
            history.replace(`/pipelines/${pipelines[0].id}`);
        }
    }, [pipelineId, pipelines, history]);

    const selectedPipeline = React.useMemo(() => {
        if (!pipelines || pipelines.length === 0) {
            return null;
        }
        return pipelines.find(p => p.id === pipelineId) ?? pipelines[0];
    }, [pipelines, pipelineId]);

    let content: React.ReactNode;
    if (pipelinesQuery.isError) {
        content = (
            <div className="flex-1 p-8">
                <ErrorMessage component="ReferralPipelinePage">
                    <pre>{JSON.stringify(pipelinesQuery.error, null, 2)}</pre>
                </ErrorMessage>
            </div>
        );
    } else if (!pipelines || !regionId) {
        content = (
            <div className="flex-1 flex items-center justify-center p-8">
                <Spinner />
            </div>
        );
    } else if (pipelines.length === 0) {
        content = <ReferralPipelinesEmptyState message="No referral pipelines yet." />;
    } else if (!selectedPipeline) {
        content = (
            <div className="flex-1 flex items-center justify-center p-8">
                <Spinner />
            </div>
        );
    } else if (selectedPipeline.stages.length === 0) {
        content = (
            <ReferralPipelinesEmptyState
                message={`"${selectedPipeline.name}" has no stages yet.`}
            />
        );
    } else {
        content = (
            <div className="pipeline-wrapper bg-brand-700 flex px-4 py-2 flex-1 h-screen md:h-auto md:overflow-hidden">
                <ReferralPipelineBoard
                    pipeline={selectedPipeline}
                    regionId={regionId}
                    boardQuery={boardQuery}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col md:inset-0 md:absolute">
            <PageHeader title="Referral Pipelines">
                {selectedPipeline && selectedPipeline.stages.length > 0 && regionId ? (
                    <AddReferralToBoardButton
                        pipeline={selectedPipeline}
                        regionId={regionId}
                    />
                ) : null}
            </PageHeader>
            {selectedPipeline && selectedPipeline.stages.length > 0 ? (
                <div className="px-4 pt-2 flex flex-wrap justify-between gap-x-4 gap-y-2 items-end action-bar">
                    <div>
                        <SearchInput
                            className="w-80"
                            type="text"
                            value={searchText}
                            onChange={handleSearchTextChange}
                            onClear={handleClearSearchText}
                            placeholder="Search contact or organization"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {canSeeReferralSourceOwners ? (
                            <div>
                                <UserSelect
                                    label="Assigned to"
                                    includeEveryone
                                    includeUnassigned
                                    value={searchState.assignee}
                                    onChange={handleAssigneeChange}
                                />
                            </div>
                        ) : null}
                        <div>
                            <SelectPro
                                label="Sort by"
                                includePlaceholderOption={false}
                                value={searchState.cardSortPreference}
                                options={referralPipelineSortOptions}
                                onChange={handleSortChange}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
            {pipelines && pipelines.length > 0 ? (
                <div className="px-4 pt-2 flex flex-wrap gap-1 lg:gap-2">
                    {pipelines.map(p => (
                        <Link
                            key={p.id}
                            to={`/pipelines/${p.id}`}
                            className={
                                p.id === selectedPipeline?.id
                                    ? "twoverride whitespace-nowrap text-brand-700 bg-brand-100 px-3 py-2 font-semibold rounded-md text-sm"
                                    : "twoverride whitespace-nowrap text-gray-500 hover:text-gray-700 hover:bg-gray-50 hover:shadow-sm px-3 py-2 font-semibold rounded-md text-sm"
                            }>
                            {p.name}
                        </Link>
                    ))}
                    {canManagePipelines ? (
                        <Link
                            to="/settings/agency/referrals#add-pipeline"
                            className="twoverride whitespace-nowrap text-gray-400 hover:text-gray-600 hover:bg-gray-50 px-3 py-2 font-normal rounded-md text-sm flex items-center space-x-1">
                            <PlusIcon className="w-4 h-4" />
                            <span>Add pipeline</span>
                        </Link>
                    ) : null}
                </div>
            ) : null}
            {content}
        </div>
    );
};

const ReferralPipelinesEmptyState: React.FC<{ message: string }> = props => {
    const history = useHistory();
    const isAdmin = useCanManagePipelines();

    const goToSettings = React.useCallback(() => {
        history.push("/settings/agency/referrals#add-pipeline");
    }, [history]);

    return (
        <div className="p-4 lg:p-8 max-w-2xl space-y-4">
            <h2 className="text-lg font-medium text-gray-900">{props.message}</h2>
            <p className="text-gray-600">
                Pipelines are boards for tracking referral sources through any process you
                define — for example, developing new contacts into active referral
                partners. Add a stage for each step in the process, then move referral
                sources through the stages.
            </p>
            {isAdmin ? (
                <PrimaryButton onClick={goToSettings}>
                    Set up referral pipelines
                </PrimaryButton>
            ) : null}
        </div>
    );
};
